import React, { useEffect, useState, useCallback } from "react";
import {
  ValidationRule,
  ComparisonRule,
  CausalityRule,
  HistoricalRule,
  manResponseType,
} from "../../interfaces";
import {
  ValidationRuleTypeEnum,
  ComparisonTypeEnum,
  PhaseDictionary,
} from "../../Enum";
import { associateValueToRule } from "../../Services/ChangeService";

interface EditRuleModalProps {
  rule: ValidationRule | ComparisonRule | CausalityRule | HistoricalRule;
  onClose: () => void;
  onSave: (updatedRule: any) => void;
  manifestNames: Record<string, string>;
  responseDescriptions: manResponseType[];
  idManResponseAvailable: number[];
}

const EditRuleModal: React.FC<EditRuleModalProps> = ({
  rule,
  onClose,
  onSave,
  manifestNames,
  responseDescriptions,
  idManResponseAvailable,
}) => {
  const [formState, setFormState] = useState<any>({
    ...rule,
    applicablePhaseIds: rule.applicablePhaseIds || [],
  });

  const [inputStates, setInputStates] = useState<Record<string, string>>({});
  const [suggestions, setSuggestions] = useState<Record<string, string[]>>({});
  const [availableOptions, setAvailableOptions] = useState<manResponseType[]>([]);
  const [firstOptions, setFirstOptions] = useState<manResponseType[]>([]);
  const [secondOptions, setSecondOptions] = useState<manResponseType[]>([]);
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !saving) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose, saving]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  useEffect(() => {
    setFormState({
      ...rule,
      applicablePhaseIds: rule.applicablePhaseIds || [],
    });
    setInputStates({
      ...("variableName" in rule
        ? { variableName: manifestNames[rule.variableName] || rule.variableName }
        : {}),
      ...("firstVariableName" in rule
        ? { firstVariableName: manifestNames[rule.firstVariableName] || rule.firstVariableName }
        : {}),
      ...("secondVariableName" in rule
        ? { secondVariableName: manifestNames[rule.secondVariableName] || rule.secondVariableName }
        : {}),
    });
    setSuggestions({});
    setErrors({});
  }, [rule, manifestNames]);

  useEffect(() => {
    if (formState.useFirstVariableValue) {
      setFormState((prev: any) => ({
        ...prev,
        secondValue: "firstValue" in prev ? prev.firstValue : undefined,
      }));
    }
  }, [formState.useFirstVariableValue]);

  // ValidationRule options
  useEffect(() => {
    const load = async () => {
      if (!formState.variableName || formState.variableName.endsWith("1")) {
        setAvailableOptions([]);
        return;
      }
      try {
        const res = await associateValueToRule(
          "",
          formState.variableName,
          formState.applicablePhaseIds,
          idManResponseAvailable,
          responseDescriptions
        );
        setAvailableOptions(responseDescriptions.filter((r) => r.Id === res[1]));
      } catch {
        setAvailableOptions([]);
      }
    };
    load();
  }, [formState.variableName, formState.applicablePhaseIds, idManResponseAvailable, responseDescriptions]);

  // CausalityRule firstOptions
  useEffect(() => {
    const loadFirst = async () => {
      if (!formState.firstVariableName || formState.firstVariableName.endsWith("1")) {
        setFirstOptions([]);
        return;
      }
      try {
        const res = await associateValueToRule(
          "",
          formState.firstVariableName,
          formState.applicablePhaseIds,
          idManResponseAvailable,
          responseDescriptions
        );
        setFirstOptions(responseDescriptions.filter((r) => r.Id === res[1]));
      } catch {
        setFirstOptions([]);
      }
    };
    loadFirst();
  }, [formState.firstVariableName, formState.applicablePhaseIds, idManResponseAvailable, responseDescriptions]);

  // CausalityRule secondOptions
  useEffect(() => {
    const loadSecond = async () => {
      if (!formState.secondVariableName || formState.secondVariableName.endsWith("1")) {
        setSecondOptions([]);
        return;
      }
      try {
        const res = await associateValueToRule(
          "",
          formState.secondVariableName,
          formState.applicablePhaseIds,
          idManResponseAvailable,
          responseDescriptions
        );
        setSecondOptions(responseDescriptions.filter((r) => r.Id === res[1]));
      } catch {
        setSecondOptions([]);
      }
    };
    loadSecond();
  }, [formState.secondVariableName, formState.applicablePhaseIds, idManResponseAvailable, responseDescriptions]);

  const handleVariableChange = (field: string, text: string) => {
    setInputStates((p) => ({ ...p, [field]: text }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
    
    if (!text.trim()) return setSuggestions((p) => ({ ...p, [field]: [] }));
    
    const matches = Object.entries(manifestNames)
      .filter(([, name]) => name.toLowerCase().includes(text.toLowerCase()))
      .map(([id]) => `${field}|${id}`)
      .slice(0, 10);
    
    setSuggestions((p) => ({ ...p, [field]: matches }));
  };

  const handleVariableSelect = (selection: string) => {
    const [field, id] = selection.split("|");
    setFormState((p: any) => ({ ...p, [field]: id }));
    setInputStates((p) => ({ ...p, [field]: manifestNames[id] }));
    setSuggestions((p) => ({ ...p, [field]: [] }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleChange = (e: any) => {
    const { name, type, value, checked, selectedOptions } = e.target;
    let newVal: any;
    
    if (type === "checkbox") {
      newVal = checked;
    } else if (name === "applicablePhaseIds") {
      newVal = Array.from(selectedOptions).map((o: any) => parseInt(o.value, 10));
    } else {
      newVal = value;
    }
    
    setFormState((p: any) => ({ ...p, [name]: newVal }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};
    
    // Validación por tipo de regla
    if ("variableName" in formState) {
      if (!formState.variableName?.trim()) {
        newErrors.variableName = "La manifestación es requerida";
      }
    }
    
    if ("firstVariableName" in formState) {
      if (!formState.firstVariableName?.trim()) {
        newErrors.firstVariableName = "La primera variable es requerida";
      }
    }
    
    if ("secondVariableName" in formState) {
      if (!formState.secondVariableName?.trim()) {
        newErrors.secondVariableName = "La segunda variable es requerida";
      }
    }
    
    if ("value" in formState) {
      if (!formState.value && formState.value !== 0) {
        newErrors.value = "El valor es requerido";
      }
    }
    
    if ("ruleType" in formState) {
      if (formState.ruleType === undefined || formState.ruleType === "") {
        newErrors.ruleType = "El tipo de regla es requerido";
      }
    }
    
    if ("comparisonType" in formState) {
      if (formState.comparisonType === undefined || formState.comparisonType === "") {
        newErrors.comparisonType = "El tipo de comparación es requerido";
      }
    }
    
    if (!formState.errorMessage?.trim()) {
      newErrors.errorMessage = "El mensaje de error es requerido";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formState]);

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    setSaving(true);
    try {
      await onSave(formState);
    } finally {
      setSaving(false);
    }
  };

  const renderEnumOptions = (enm: any) =>
    Object.entries(enm)
      .filter(([k]) => !isNaN(Number(k)))
      .map(([k, v]) => (
        <option key={k} value={k}>
          {String(v)}
        </option>
      ));

  const InputWithError: React.FC<{
    id: string;
    name: string;
    value: string;
    onChange: (e: any) => void;
    placeholder?: string;
    label: string;
    error?: string;
    disabled?: boolean;
    type?: string;
  }> = ({ id, name, value, onChange, placeholder, label, error, disabled, type = "text" }) => (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-sm font-medium text-gray-700">
        {label} {error && <span className="text-red-500">*</span>}
      </label>
      <input
        id={id}
        name={name}
        type={type}
        value={value || ""}
        onChange={onChange}
        disabled={disabled}
        placeholder={placeholder}
        className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 transition-all ${
          error
            ? "border-red-500 focus:ring-red-400 bg-red-50"
            : disabled
            ? "border-gray-300 bg-gray-100"
            : "border-gray-300 focus:ring-blue-400 bg-white"
        }`}
      />
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );

  const SelectWithError: React.FC<{
    id: string;
    name: string;
    value: any;
    onChange: (e: any) => void;
    label: string;
    error?: string;
    children: React.ReactNode;
  }> = ({ id, name, value, onChange, label, error, children }) => (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-sm font-medium text-gray-700">
        {label} {error && <span className="text-red-500">*</span>}
      </label>
      <select
        id={id}
        name={name}
        value={value || ""}
        onChange={onChange}
        className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 transition-all ${
          error
            ? "border-red-500 focus:ring-red-400 bg-red-50"
            : "border-gray-300 focus:ring-blue-400 bg-white"
        }`}
      >
        {children}
      </select>
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );

  const renderValidation = () => (
    <>
      {/* Manifestación con autocomplete */}
      <div className="space-y-1">
        <label htmlFor="variableName" className="block text-sm font-medium text-gray-700">
          Manifestación {errors.variableName && <span className="text-red-500">*</span>}
        </label>
        <div className="relative">
          <input
            id="variableName"
            value={inputStates.variableName || ""}
            onChange={(e) => handleVariableChange("variableName", e.target.value)}
            onBlur={() => setTimeout(() => setSuggestions((p) => ({ ...p, variableName: [] })), 200)}
            className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 transition-all ${
              errors.variableName
                ? "border-red-500 focus:ring-red-400 bg-red-50"
                : "border-gray-300 focus:ring-blue-400 bg-white"
            }`}
            placeholder="🔍 Buscar manifestación..."
          />
          {suggestions.variableName && suggestions.variableName.length > 0 && (
            <ul className="absolute z-20 w-full bg-white border border-gray-300 rounded-lg mt-1 max-h-60 overflow-auto shadow-xl">
              {suggestions.variableName.map((s) => {
                const id = s.split("|")[1];
                return (
                  <li
                    key={s}
                    onClick={() => handleVariableSelect(s)}
                    className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm transition-colors"
                  >
                    {manifestNames[id]}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        {errors.variableName && <p className="text-xs text-red-600">{errors.variableName}</p>}
      </div>

      {/* Tipo de Regla */}
      <SelectWithError
        id="ruleType"
        name="ruleType"
        value={formState.ruleType}
        onChange={handleChange}
        label="Tipo de Regla"
        error={errors.ruleType}
      >
        <option value="">Seleccionar...</option>
        {renderEnumOptions(ValidationRuleTypeEnum)}
      </SelectWithError>

      {/* Valor */}
      <div className="space-y-1">
        <label htmlFor="value" className="block text-sm font-medium text-gray-700">
          Valor {errors.value && <span className="text-red-500">*</span>}
        </label>
        {availableOptions.length > 0 ? (
          <select
            id="value"
            name="value"
            value={formState.value || ""}
            onChange={handleChange}
            className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 transition-all ${
              errors.value
                ? "border-red-500 focus:ring-red-400 bg-red-50"
                : "border-gray-300 focus:ring-blue-400 bg-white"
            }`}
          >
            <option value="">Seleccionar...</option>
            {availableOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.description_es} ({o.id})
              </option>
            ))}
          </select>
        ) : (
          <input
            id="value"
            name="value"
            value={formState.value || ""}
            onChange={handleChange}
            placeholder="Ingrese el valor..."
            className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 transition-all ${
              errors.value
                ? "border-red-500 focus:ring-red-400 bg-red-50"
                : "border-gray-300 focus:ring-blue-400 bg-white"
            }`}
          />
        )}
        {errors.value && <p className="text-xs text-red-600">{errors.value}</p>}
      </div>

      {/* Mensaje de Error */}
      <InputWithError
        id="errorMessage"
        name="errorMessage"
        value={formState.errorMessage}
        onChange={handleChange}
        placeholder="Describa el mensaje de error..."
        label="Mensaje de Error"
        error={errors.errorMessage}
      />
    </>
  );

  const renderComparison = () => (
    <>
      {/* Primera Variable */}
      <div className="space-y-1">
        <label htmlFor="firstVariableName" className="block text-sm font-medium text-gray-700">
          Primera Variable {errors.firstVariableName && <span className="text-red-500">*</span>}
        </label>
        <div className="relative">
          <input
            id="firstVariableName"
            value={inputStates.firstVariableName || ""}
            onChange={(e) => handleVariableChange("firstVariableName", e.target.value)}
            onBlur={() => setTimeout(() => setSuggestions((p) => ({ ...p, firstVariableName: [] })), 200)}
            placeholder="🔍 Buscar primera variable..."
            className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 transition-all ${
              errors.firstVariableName
                ? "border-red-500 focus:ring-red-400 bg-red-50"
                : "border-gray-300 focus:ring-blue-400 bg-white"
            }`}
          />
          {suggestions.firstVariableName && suggestions.firstVariableName.length > 0 && (
            <ul className="absolute z-20 w-full bg-white border border-gray-300 rounded-lg mt-1 max-h-60 overflow-auto shadow-xl">
              {suggestions.firstVariableName.map((s) => {
                const id = s.split("|")[1];
                return (
                  <li
                    key={s}
                    onClick={() => handleVariableSelect(s)}
                    className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm transition-colors"
                  >
                    {manifestNames[id]}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        {errors.firstVariableName && <p className="text-xs text-red-600">{errors.firstVariableName}</p>}
      </div>

      {/* Segunda Variable */}
      <div className="space-y-1">
        <label htmlFor="secondVariableName" className="block text-sm font-medium text-gray-700">
          Segunda Variable {errors.secondVariableName && <span className="text-red-500">*</span>}
        </label>
        <div className="relative">
          <input
            id="secondVariableName"
            value={inputStates.secondVariableName || ""}
            onChange={(e) => handleVariableChange("secondVariableName", e.target.value)}
            onBlur={() => setTimeout(() => setSuggestions((p) => ({ ...p, secondVariableName: [] })), 200)}
            placeholder="🔍 Buscar segunda variable..."
            className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 transition-all ${
              errors.secondVariableName
                ? "border-red-500 focus:ring-red-400 bg-red-50"
                : "border-gray-300 focus:ring-blue-400 bg-white"
            }`}
          />
          {suggestions.secondVariableName && suggestions.secondVariableName.length > 0 && (
            <ul className="absolute z-20 w-full bg-white border border-gray-300 rounded-lg mt-1 max-h-60 overflow-auto shadow-xl">
              {suggestions.secondVariableName.map((s) => {
                const id = s.split("|")[1];
                return (
                  <li
                    key={s}
                    onClick={() => handleVariableSelect(s)}
                    className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm transition-colors"
                  >
                    {manifestNames[id]}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        {errors.secondVariableName && <p className="text-xs text-red-600">{errors.secondVariableName}</p>}
      </div>

      {/* Tipo de Comparación */}
      <SelectWithError
        id="comparisonType"
        name="comparisonType"
        value={formState.comparisonType}
        onChange={handleChange}
        label="Tipo de Comparación"
        error={errors.comparisonType}
      >
        <option value="">Seleccionar...</option>
        {renderEnumOptions(ComparisonTypeEnum)}
      </SelectWithError>

      {/* Mensaje de Error */}
      <InputWithError
        id="errorMessage"
        name="errorMessage"
        value={formState.errorMessage}
        onChange={handleChange}
        placeholder="Describa el mensaje de error..."
        label="Mensaje de Error"
        error={errors.errorMessage}
      />
    </>
  );

  const renderCausality = () => (
    <>
      {/* Primera Variable */}
      <div className="space-y-1">
        <label htmlFor="firstVariableName" className="block text-sm font-medium text-gray-700">
          Primera Variable {errors.firstVariableName && <span className="text-red-500">*</span>}
        </label>
        <div className="relative">
          <input
            id="firstVariableName"
            value={inputStates.firstVariableName || ""}
            onChange={(e) => handleVariableChange("firstVariableName", e.target.value)}
            onBlur={() => setTimeout(() => setSuggestions((p) => ({ ...p, firstVariableName: [] })), 200)}
            placeholder="🔍 Buscar primera variable..."
            className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 transition-all ${
              errors.firstVariableName
                ? "border-red-500 focus:ring-red-400 bg-red-50"
                : "border-gray-300 focus:ring-blue-400 bg-white"
            }`}
          />
          {suggestions.firstVariableName && suggestions.firstVariableName.length > 0 && (
            <ul className="absolute z-20 w-full bg-white border border-gray-300 rounded-lg mt-1 max-h-60 overflow-auto shadow-xl">
              {suggestions.firstVariableName.map((s) => {
                const id = s.split("|")[1];
                return (
                  <li
                    key={s}
                    onClick={() => handleVariableSelect(s)}
                    className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm transition-colors"
                  >
                    {manifestNames[id]}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        {errors.firstVariableName && <p className="text-xs text-red-600">{errors.firstVariableName}</p>}
      </div>

      {/* Condición 1 */}
      <SelectWithError
        id="firstCondition"
        name="firstCondition"
        value={formState.firstCondition}
        onChange={handleChange}
        label="Condición 1"
        error={errors.firstCondition}
      >
        <option value="">Seleccionar...</option>
        {renderEnumOptions(ValidationRuleTypeEnum)}
      </SelectWithError>

      {/* Valor 1 */}
      <div className="space-y-1">
        <label htmlFor="firstValue" className="block text-sm font-medium text-gray-700">
          Valor 1
        </label>
        {firstOptions.length > 0 ? (
          <select
            id="firstValue"
            name="firstValue"
            value={formState.firstValue || ""}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
          >
            <option value="">Seleccionar...</option>
            {firstOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.description_es} ({o.id})
              </option>
            ))}
          </select>
        ) : (
          <input
            id="firstValue"
            name="firstValue"
            value={formState.firstValue || ""}
            onChange={handleChange}
            placeholder="Ingrese valor 1"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
          />
        )}
      </div>

      {/* Segunda Variable */}
      <div className="space-y-1">
        <label htmlFor="secondVariableName" className="block text-sm font-medium text-gray-700">
          Segunda Variable {errors.secondVariableName && <span className="text-red-500">*</span>}
        </label>
        <div className="relative">
          <input
            id="secondVariableName"
            value={inputStates.secondVariableName || ""}
            onChange={(e) => handleVariableChange("secondVariableName", e.target.value)}
            onBlur={() => setTimeout(() => setSuggestions((p) => ({ ...p, secondVariableName: [] })), 200)}
            placeholder="🔍 Buscar segunda variable..."
            className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 transition-all ${
              errors.secondVariableName
                ? "border-red-500 focus:ring-red-400 bg-red-50"
                : "border-gray-300 focus:ring-blue-400 bg-white"
            }`}
          />
          {suggestions.secondVariableName && suggestions.secondVariableName.length > 0 && (
            <ul className="absolute z-20 w-full bg-white border border-gray-300 rounded-lg mt-1 max-h-60 overflow-auto shadow-xl">
              {suggestions.secondVariableName.map((s) => {
                const id = s.split("|")[1];
                return (
                  <li
                    key={s}
                    onClick={() => handleVariableSelect(s)}
                    className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm transition-colors"
                  >
                    {manifestNames[id]}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        {errors.secondVariableName && <p className="text-xs text-red-600">{errors.secondVariableName}</p>}
      </div>

      {/* Condición 2 */}
      <SelectWithError
        id="secondCondition"
        name="secondCondition"
        value={formState.secondCondition}
        onChange={handleChange}
        label="Condición 2"
        error={errors.secondCondition}
      >
        <option value="">Seleccionar...</option>
        {renderEnumOptions(ValidationRuleTypeEnum)}
      </SelectWithError>

      {/* Valor 2 */}
      <div className="space-y-1">
        <label htmlFor="secondValue" className="block text-sm font-medium text-gray-700">
          Valor 2
        </label>
        {secondOptions.length > 0 && !formState.useFirstVariableValue ? (
          <select
            id="secondValue"
            name="secondValue"
            value={formState.secondValue || ""}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
          >
            <option value="">Seleccionar...</option>
            {secondOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.description_es} ({o.id})
              </option>
            ))}
          </select>
        ) : (
          <input
            id="secondValue"
            name="secondValue"
            disabled={formState.useFirstVariableValue}
            value={formState.secondValue || ""}
            onChange={handleChange}
            placeholder={formState.useFirstVariableValue ? "Usando valor 1" : "Ingrese valor 2"}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 bg-gray-100"
          />
        )}
      </div>

      {/* Checkbox: Usar Valor 1 */}
      <div className="flex items-center space-x-2">
        <input
          id="useFirstVariableValue"
          type="checkbox"
          name="useFirstVariableValue"
          checked={formState.useFirstVariableValue || false}
          onChange={handleChange}
          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
        />
        <label htmlFor="useFirstVariableValue" className="text-sm font-medium text-gray-700">
          Usar Valor de la Primera Variable
        </label>
      </div>

      {/* Mensaje de Error */}
      <InputWithError
        id="errorMessage"
        name="errorMessage"
        value={formState.errorMessage}
        onChange={handleChange}
        placeholder="Describa el mensaje de error..."
        label="Mensaje de Error"
        error={errors.errorMessage}
      />
    </>
  );

  const renderHistorical = () => (
    <>
      {/* Manifestación */}
      <div className="space-y-1">
        <label htmlFor="variableName" className="block text-sm font-medium text-gray-700">
          Manifestación {errors.variableName && <span className="text-red-500">*</span>}
        </label>
        <div className="relative">
          <input
            id="variableName"
            value={inputStates.variableName || ""}
            onChange={(e) => handleVariableChange("variableName", e.target.value)}
            onBlur={() => setTimeout(() => setSuggestions((p) => ({ ...p, variableName: [] })), 200)}
            placeholder="🔍 Buscar manifestación..."
            className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 transition-all ${
              errors.variableName
                ? "border-red-500 focus:ring-red-400 bg-red-50"
                : "border-gray-300 focus:ring-blue-400 bg-white"
            }`}
          />
          {suggestions.variableName && suggestions.variableName.length > 0 && (
            <ul className="absolute z-20 w-full bg-white border border-gray-300 rounded-lg mt-1 max-h-60 overflow-auto shadow-xl">
              {suggestions.variableName.map((s) => {
                const id = s.split("|")[1];
                return (
                  <li
                    key={s}
                    onClick={() => handleVariableSelect(s)}
                    className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm transition-colors"
                  >
                    {manifestNames[id]}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        {errors.variableName && <p className="text-xs text-red-600">{errors.variableName}</p>}
      </div>

      {/* Prefijo Variables Relacionadas */}
      <InputWithError
        id="relatedVariablesPrefix"
        name="relatedVariablesPrefix"
        value={formState.relatedVariablesPrefix}
        onChange={handleChange}
        placeholder="Ej: Var_"
        label="Prefijo de Variables Relacionadas"
        error={errors.relatedVariablesPrefix}
      />

      {/* Condición Relacionada */}
      <SelectWithError
        id="relatedCondition"
        name="relatedCondition"
        value={formState.relatedCondition}
        onChange={handleChange}
        label="Condición Relacionada"
        error={errors.relatedCondition}
      >
        <option value="">Seleccionar...</option>
        {renderEnumOptions(ComparisonTypeEnum)}
      </SelectWithError>

      {/* Valor Relacionado */}
      <InputWithError
        id="relatedValue"
        name="relatedValue"
        value={formState.relatedValue}
        onChange={handleChange}
        placeholder="Valor para la condición relacionada"
        label="Valor Relacionado"
        error={errors.relatedValue}
      />

      {/* Mensaje de Error */}
      <InputWithError
        id="errorMessage"
        name="errorMessage"
        value={formState.errorMessage}
        onChange={handleChange}
        placeholder="Describa el mensaje de error..."
        label="Mensaje de Error"
        error={errors.errorMessage}
      />
    </>
  );

  const renderPhases = () => (
    <div className="md:col-span-2 space-y-1">
      <label htmlFor="applicablePhaseIds" className="block text-sm font-medium text-gray-700">
        Fases Aplicables
      </label>
      <select
        id="applicablePhaseIds"
        name="applicablePhaseIds"
        multiple
        value={formState.applicablePhaseIds.map((n: number) => n.toString())}
        onChange={handleChange}
        className="w-full h-32 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
      >
        {Object.entries(PhaseDictionary).map(([key, disp]) => (
          <option key={key} value={key}>
            Fase {disp}
          </option>
        ))}
      </select>
      <p className="text-xs text-gray-500">💡 Mantén presionado Ctrl/Cmd para seleccionar múltiples fases</p>
    </div>
  );

  const isValidation = "ruleType" in formState;
  const isComparison = "comparisonType" in formState;
  const isCausality = "firstCondition" in formState && "secondCondition" in formState;
  const isHistorical = "relatedCondition" in formState;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && !saving && onClose()}
    >
      <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-3xl space-y-6 overflow-auto max-h-[90vh]">
        <div className="flex justify-between items-center border-b pb-3">
          <h2 className="text-2xl font-bold text-gray-800">✏️ Editar Regla</h2>
          <button
            onClick={onClose}
            disabled={saving}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold transition-colors"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <div className="space-y-4">
          {isValidation && renderValidation()}
          {isComparison && renderComparison()}
          {isCausality && renderCausality()}
          {isHistorical && renderHistorical()}

          {renderPhases()}
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
          >
            {saving && <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>}
            <span>{saving ? "Guardando..." : "Guardar Cambios"}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditRuleModal;